import React from 'react';

import { Modal } from 'antd';

import styled from 'styled-components';

import {
    ARTICLE_ROW_LAYOUT,
    ARTICLE_ROW_LAYOUT_OPTIONS,
} from '@containers/Art/Articles/Articles.constants';

const PickerGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(168px, 1fr));
    gap: 0.85rem;
    margin-top: 0.25rem;
`;

const OptionCard = styled.button`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.55rem;
    padding: 0.8rem 0.75rem;
    border: ${(props) => props.theme.DefaultBorder};
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    text-align: left;
    font: inherit;
    transition:
        border-color 0.15s ease,
        box-shadow 0.15s ease;

    &:hover {
        border-color: ${(props) => props.theme.PrimaryColor};
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07);
    }

    ${(props) =>
        props.$active
            ? `
        border-color: ${props.theme.PrimaryColor};
        box-shadow: 0 0 0 2px ${props.theme.PrimaryColor}33;
    `
            : ''}
`;

const Diagram = styled.div`
    display: flex;
    gap: 4px;
    min-height: 48px;
    align-items: stretch;
`;

const DiagramSeg = styled.div`
    flex: ${(props) => props.$grow} 1 0;
    min-width: 0;
    border-radius: 5px;
    background: ${(props) => props.theme.LightBlueBackground};
    border: 1px solid ${(props) => props.theme.PrimaryColor}40;
`;

const OptionLabel = styled.div`
    font-size: ${(props) => props.theme.FontSizeSm};
    font-weight: 600;
    color: ${(props) => props.theme.TextColor};
    line-height: 1.3;
`;

/** flex-grow values per segment for each layout */
const LAYOUT_DIAGRAM_SEGMENTS = {
    [ARTICLE_ROW_LAYOUT.ONE_COL]: [1],
    [ARTICLE_ROW_LAYOUT.TWO_33_66]: [1, 2],
    [ARTICLE_ROW_LAYOUT.TWO_50_50]: [1, 1],
    [ARTICLE_ROW_LAYOUT.TWO_66_33]: [2, 1],
    [ARTICLE_ROW_LAYOUT.THREE_COL]: [1, 1, 1],
};

const LayoutDiagram = ({ value }) => {
    const segments = LAYOUT_DIAGRAM_SEGMENTS[value] || [1];

    return (
        <Diagram aria-hidden>
            {segments.map((grow, index) => (
                <DiagramSeg key={index} $grow={grow} />
            ))}
        </Diagram>
    );
};

/**
 * Modal to pick an article row layout (add row or change existing row).
 *
 * @param {object} props
 * @param {boolean} props.visible
 * @param {string | undefined} props.activeLayout Current layout key (highlights matching card).
 * @param {number} [props.zIndex]
 * @param {() => void} props.onCancel
 * @param {(layoutValue: string) => void} props.onSelect
 */
const ArticleRowLayoutModal = ({ visible, activeLayout, zIndex, onCancel, onSelect }) => {
    return (
        <Modal
            destroyOnClose
            footer={null}
            title="Row layout"
            visible={visible}
            width={720}
            zIndex={zIndex}
            onCancel={onCancel}
        >
            <PickerGrid>
                {ARTICLE_ROW_LAYOUT_OPTIONS.map((option) => (
                    <OptionCard
                        key={option.value}
                        aria-label={option.label}
                        $active={activeLayout === option.value}
                        type="button"
                        onClick={() => {
                            onSelect(option.value);
                            onCancel();
                        }}
                    >
                        <LayoutDiagram value={option.value} />
                        <OptionLabel>{option.label}</OptionLabel>
                    </OptionCard>
                ))}
            </PickerGrid>
        </Modal>
    );
};

export default ArticleRowLayoutModal;
